import {applyStaticTemplate} from 'analysis-tool/AnalysisActions';
import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import isExperimentOptimizationEnabled from 'machine-learning/isExperimentOptimizationEnabled';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import ConfigureStaticTemplateModal from 'pipelines/static-templates/ConfigureStaticTemplateModal';
import {StaticTemplateId} from 'pipelines/static-templates/GeneratedStaticTemplateUIShapes';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import {isDataFrameView} from '../AnalysisViewUtils';

export default function getExperimentOptimizationConfigurationAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
): OverflowDataViewAction | null {
  const {step, stepOutputReference} = view.lineage[view.lineage.length - 1];
  if (isAnalysisReadOnly(analysis) || !isExperimentOptimizationEnabled() || !isDataFrameView(view)) {
    return null;
  }

  const inputDataFrame = stepOutputReference!.outputStepData.rawDatas[
    stepOutputReference!.rawDataIndex!
  ] as PipelineStepRawData<PipelineStepDataKind.DATASET>;

  const {canEdit, message} = checkCanModifyStep(analysis, step.id, 'table', false);

  return {
    icon: 'settings',
    onClick: () =>
      runModal(ConfigureStaticTemplateModal, {
        inputDataFrames: [inputDataFrame],
        onRequestSubmit: (config: Record<string, JSONValue>) => {
          handleConfigureExperimentOptimization(
            analysis.id,
            step.id,
            stepOutputReference!.outputStepData.name,
            stepOutputReference!.rawDataIndex!,
            config,
            onChangeSelectedItem
          );
        },
        staticTemplateId: StaticTemplateId.EXPERIMENT_OPTIMIZATION,
      }),
    label: 'Configure Experiment Optimization',
    isDisabled: !canEdit,
    helpText: !canEdit ? message : undefined,
  };
}

export async function handleConfigureExperimentOptimization(
  analysisId: string,
  inputStepId: string,
  inputStepDataName: string,
  inputRawDataIndex: number,
  config: Record<string, JSONValue>,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
): Promise<void> {
  const newSteps = await withLoadingScreen(
    applyStaticTemplate(analysisId, {
      staticTemplateId: 'EXPERIMENT_OPTIMIZATION',
      variableValues: config,
      inputReferences: [
        {
          fromStepId: inputStepId,
          fromStepDataName: inputStepDataName,
          fromStepDataIndex: inputRawDataIndex,
        },
      ],
    }),
    'Initializing Experiment Optimization...'
  );

  onChangeSelectedItem({
    type: AnalysisMainViewItemType.DATA_VIEW,
    viewReference: {
      stepId: newSteps[0].id,
      outputReference: {
        stepDataName: newSteps[0].outputStepDatas[0].name,
        rawDataIndex: 0,
      },
    },
  });
}
